import { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import { api, setToken, clearToken, formatApiErrorDetail } from "@/lib/api";
import { logger } from "@/lib/logger";

const AuthContext = createContext(null);

const errResult = (e) => ({ ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message });

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = unauthenticated, object = authed
  const [loading, setLoading] = useState(true);

  const loadMe = useCallback(async () => {
    // Always validate via /auth/me — auth is carried by the httpOnly cookie,
    // so this works across reloads even though the in-memory token is gone.
    // Only a 401/403 means "unauthenticated"; transient network/5xx errors are
    // retried so a momentary blip at boot does not log a valid user out.
    for (let attempt = 0; attempt < 3; attempt++) {
      try {
        const { data } = await api.get("/auth/me", { _silent: true });
        setUser(data);
        setLoading(false);
        return;
      } catch (e) {
        const status = e.response?.status;
        if (status === 401 || status === 403) {
          clearToken();
          setUser(false);
          setLoading(false);
          return;
        }
        logger.error(`Auth check failed (attempt ${attempt + 1}/3):`, e);
        await new Promise((r) => setTimeout(r, 600 * (attempt + 1)));
      }
    }
    // Could not confirm auth after retries — fall back to unauthenticated.
    setUser(false);
    setLoading(false);
  }, []);

  useEffect(() => {
    loadMe();
  }, [loadMe]);

  const login = useCallback(async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      // MFA gate: server withheld the session — surface the challenge to the UI.
      if (data?.mfa_required && data?.challenge_token) {
        return {
          ok: false,
          mfaRequired: true,
          challengeToken: data.challenge_token,
          expiresIn: data.expires_in,
          email,
        };
      }
      setToken(data.access_token);
      setUser(data.user);
      return { ok: true, user: data.user, mfaSetupRequired: !!data.mfa_setup_required };
    } catch (e) {
      return errResult(e);
    }
  }, []);

  const verifyMfa = useCallback(async (challengeToken, code) => {
    try {
      const { data } = await api.post("/auth/mfa/verify", {
        challenge_token: challengeToken,
        code,
      });
      setToken(data.access_token);
      setUser(data.user);
      return { ok: true, user: data.user };
    } catch (e) {
      return errResult(e);
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch (e) {
      logger.error("Logout request failed (clearing session anyway):", e);
    }
    clearToken();
    setUser(false);
  }, []);

  const forgotPassword = useCallback(async (email) => {
    try {
      const { data } = await api.post("/auth/forgot-password", { email });
      return { ok: true, data };
    } catch (e) {
      return errResult(e);
    }
  }, []);

  const resetPassword = useCallback(async (token, newPassword) => {
    try {
      const { data } = await api.post("/auth/reset-password", { token, new_password: newPassword });
      return { ok: true, data };
    } catch (e) {
      return errResult(e);
    }
  }, []);

  const changePassword = useCallback(async (currentPassword, newPassword) => {
    try {
      const { data } = await api.post("/auth/change-password", {
        current_password: currentPassword,
        new_password: newPassword,
      });
      if (data?.access_token) setToken(data.access_token);
      return { ok: true };
    } catch (e) {
      return errResult(e);
    }
  }, []);

  const acceptInvite = useCallback(async (token, newPassword) => {
    try {
      const { data } = await api.post("/auth/accept-invite", { token, new_password: newPassword });
      setToken(data.access_token);
      setUser(data.user);
      return { ok: true, user: data.user };
    } catch (e) {
      return errResult(e);
    }
  }, []);

  const can = useCallback((perm) => {
    if (!user || !user.permissions) return false;
    return user.permissions.includes("*") || user.permissions.includes(perm);
  }, [user]);

  const value = useMemo(
    () => ({ user, loading, login, verifyMfa, logout, can, refresh: loadMe, forgotPassword, resetPassword, changePassword, acceptInvite }),
    [user, loading, login, verifyMfa, logout, can, loadMe, forgotPassword, resetPassword, changePassword, acceptInvite]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
